// Pricing & Usage logic shared by today's pane (PricingTab) and the full unit
// page (UnitV2/tabs/PricingTabV2): the price-change summary and the points
// of the price history line.

export const DAY_MS = 86400000;

/** A price move: an increase is "up" (green), a reduction "down" (orange). */
export const priceChangeTone = (pct) => {
    if (pct > 0) return 'up';
    if (pct < 0) return 'down';
    return 'flat';
};

export const fmtPricePct = (pct) => {
    if (pct == null || Number.isNaN(pct)) return '—';
    const sign = pct > 0 ? '+' : pct < 0 ? '−' : '';
    return `${sign}${Math.abs(pct).toFixed(1)}%`;
};

export const fmtHistDate = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/**
 * The price line: the original price at the listing date (DATEREC, else
 * today − days_on_market, else yesterday), each change's new price at its
 * date, then the current price at `now`. [{ t, price, kind: origin|change|current }]
 */
export function priceHistoryPoints(unit, history, now = Date.now()) {
    let t0 = unit?.DATEREC ? new Date(unit.DATEREC).getTime() : NaN;
    if (Number.isNaN(t0) && history.days_on_market != null) t0 = now - history.days_on_market * DAY_MS;
    if (Number.isNaN(t0)) t0 = now - DAY_MS;
    const pts = [{ t: t0, price: history.original_price, kind: 'origin' }];
    (history.changes || []).forEach((c) => {
        const ct = new Date(c.date).getTime();
        pts.push({ t: Number.isNaN(ct) ? t0 : ct, price: c.new_price, kind: 'change' });
    });
    pts.push({ t: now, price: history.current_price, kind: 'current' });
    return pts;
}

/** The total change in dollars (current − original), or null. */
export const priceChangeDollars = (history) => (
    history && typeof history.current_price === 'number' && typeof history.original_price === 'number'
        ? history.current_price - history.original_price
        : null
);

/** Changes, newest first. */
export const changesNewestFirst = (changes) => [...(changes || [])].reverse();

/**
 * The 12-month value change from the forward predictions, the way the
 * Intelligence trajectory reads it: today's value (PredictedPrice ‖
 * MLPrediction, else the first forward month) to month 12, with forward
 * values ≤ 0 dropped and at least 2 needed. Null otherwise.
 */
export function twelveMonthValueChange(unit, predictions12m) {
    const forward = (predictions12m?.predictions || []).filter((p) => p != null && p > 0);
    if (forward.length < 2) return null;
    const today = unit?.PredictedPrice || unit?.MLPrediction || null;
    const prices = today ? [today, ...forward] : forward;
    return prices[prices.length - 1] - prices[0];
}
